import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RECORD_DATA_TYPE } from '@demandproai/shared';
import LoggedInUserService from '../services/AuthService';
import { getRequest, putRequest, deleteRequest } from '../utils/axiosClient';
import { toaster } from '../utils/toast';
import { validateSignatureFile } from '../utils/signatureValidation';
import useUploadQueue from './useUploadQueue';
import { useDownloadOptions } from './useDownloadOptions';
import Constants from '../Constants';

const useSignatureUpload = () => {
  const userData = LoggedInUserService.GetLoggedInUserData();
  const { downloadSingleFile } = useDownloadOptions();
  const { queueFiles, isLoading: isQueuingPresigned } = useUploadQueue(userData?.id);
  const uploadQueue = useSelector((state) => state.fileUpload?.uploadQueue ?? []);
  const activeUploads = useSelector((state) => state.fileUpload?.activeUploads ?? {});
  const completedUploads = useSelector((state) => state.fileUpload?.completedUploads ?? []);
  const failedUploads = useSelector((state) => state.fileUpload?.failedUploads ?? []);
  const isUploadPending = uploadQueue.length > 0 || Object.keys(activeUploads).length > 0;

  const [signature, setSignature] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  // tracks the uploadId we kicked off so the post-upload effect only fires for our file
  const pendingUploadRef = useRef(null);

  const isUploading = isQueuingPresigned || isUploadPending || isFinalizing || isDeleting;

  const fetchSignature = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getRequest({ url: Constants.ApiUrl.signature.get });
      setSignature(response?.data?.data?.signature || null);
    } catch (err) {
      console.error('Error fetching signature:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSignature();
  }, [fetchSignature]);

  const finalizeSignature = useCallback(async ({ uniqueKey }) => {
    setIsFinalizing(true);
    try {
      const response = await putRequest({
        url: Constants.ApiUrl.signature.put,
        data: { s3UrlPath: uniqueKey }
      });
      await fetchSignature();
      toaster({ message: 'Signature uploaded successfully.', success: true });
      const warnings = response?.data?.data?.warnings || [];
      warnings.forEach((message) => toaster({ message, variant: 'warning' }));
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to upload signature. Please try again.';
      toaster({ message, success: false });
    } finally {
      setIsFinalizing(false);
    }
  }, [fetchSignature]);

  useEffect(() => {
    const pending = pendingUploadRef.current;
    if (!pending || isUploadPending) return;
    const failed = failedUploads.find((f) => f.id === pending.id);
    if (failed) {
      pendingUploadRef.current = null;
      return;
    }
    const completed = completedUploads.find((c) => c.id === pending.id);
    if (!completed) return;
    pendingUploadRef.current = null;
    finalizeSignature({ uniqueKey: pending.uniqueKey });
  }, [isUploadPending, completedUploads, failedUploads, finalizeSignature]);

  const performUpload = useCallback(async ({ file }) => {
    try {
      const prepFiles = [{ file, name: file.name, metaData: {} }];
      const { uploadTasks } = await queueFiles({
        files: prepFiles,
        caseRecordType: RECORD_DATA_TYPE.PLANNER_SIGNATURE
      });
      const task = uploadTasks?.[0];
      if (!task) throw new Error('Failed to enqueue signature upload.');
      pendingUploadRef.current = { id: task.id, uniqueKey: task.uniqueKey };
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to upload signature. Please try again.';
      toaster({ message, success: false });
    }
  }, [queueFiles]);

  const performDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteRequest({ url: Constants.ApiUrl.signature.delete });
      await fetchSignature();
      toaster({ message: 'Signature removed successfully.', success: true });
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to remove signature. Please try again.';
      toaster({ message, success: false });
    } finally {
      setIsDeleting(false);
    }
  }, [fetchSignature]);

  const uploadSignature = useCallback(async (file) => {
    if (isUploading) return;
    const validation = await validateSignatureFile({ file });
    if (!validation.isValid) {
      toaster({ message: validation.error, success: false });
      return;
    }
    await performUpload({ file });
  }, [isUploading, performUpload]);

  const onFileSelect = useCallback(async ({ file }) => {
    const picked = file?.[0];
    if (!picked) return;
    await uploadSignature(picked);
  }, [uploadSignature]);

  const downloadSignature = useCallback(() => {
    if (!signature?.s3UrlPath) return;
    return downloadSingleFile({ name: 'signature.png', s3UrlPath: signature.s3UrlPath });
  }, [downloadSingleFile, signature?.s3UrlPath]);

  const removeSignature = useCallback(() => {
    if (!signature || isUploading) return;
    setConfirmation({
      type: 'delete',
      isOpen: true,
      onConfirm: async () => {
        setConfirmation(null);
        await performDelete();
      },
      onCancel: () => setConfirmation(null)
    });
  }, [isUploading, performDelete, signature]);

  return {
    signature,
    isLoading,
    isUploading,
    uploadSignature,
    onFileSelect,
    removeSignature,
    downloadSignature,
    confirmation
  };
};

export default useSignatureUpload;
